import { useState, useEffect, useCallback, useRef } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  getMultiFactorResolver,
  GoogleAuthProvider,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  User,
} from 'firebase/auth'
import { auth } from '../firebase'

const googleProvider = new GoogleAuthProvider()
// Sempre mostrar o seletor de contas, mesmo com uma única sessão Google ativa
googleProvider.setCustomParameters({ prompt: 'select_account' })

export const RECAPTCHA_CONTAINER_ID = 'recaptcha-container'

const RESEND_COOLDOWN_SECONDS = 30

export interface MfaState {
  phoneHint: string | null
  sending: boolean
  verifying: boolean
  resendCooldown: number
}

interface UseAuthReturn {
  user: User | null
  loading: boolean
  error: string | null
  mfa: MfaState | null
  signInWithGoogle: () => Promise<void>
  verifyMfaCode: (code: string) => Promise<void>
  resendMfaCode: () => Promise<void>
  cancelMfa: () => void
  signOut: () => Promise<void>
}

function describeAuthError(code: string | undefined): string {
  switch (code) {
    case 'auth/popup-blocked':
      return 'O navegador bloqueou o popup de login. Libere popups para este site e tente novamente.'
    case 'auth/network-request-failed':
      return 'Falha de rede ao fazer login. Verifique sua conexão e tente novamente.'
    case 'auth/user-disabled':
      return 'Esta conta está desativada. Fale com um administrador.'
    case 'auth/too-many-requests':
      return 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.'
    case 'auth/invalid-verification-code':
      return 'Código incorreto. Confira o SMS e tente novamente.'
    case 'auth/code-expired':
      return 'O código expirou. Reenvie o SMS e tente novamente.'
    default:
      return `Erro ao fazer login${code ? ` (${code})` : ''}. Tente novamente.`
  }
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mfa, setMfa] = useState<MfaState | null>(null)

  const resolverRef = useRef<MultiFactorResolver | null>(null)
  const verificationIdRef = useRef<string | null>(null)
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null)
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        if (firebaseUser.email?.endsWith('@widde.io')) {
          setUser(firebaseUser)
          setError(null)
        } else {
          firebaseSignOut(auth)
          setUser(null)
          setError('Acesso restrito a contas @widde.io')
        }
      } else {
        setUser(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const cleanupRecaptcha = useCallback(() => {
    if (recaptchaRef.current) {
      recaptchaRef.current.clear()
      recaptchaRef.current = null
    }
  }, [])

  const stopCooldown = useCallback(() => {
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current)
      cooldownIntervalRef.current = null
    }
  }, [])

  const startCooldown = useCallback(() => {
    stopCooldown()
    cooldownIntervalRef.current = setInterval(() => {
      setMfa((prev) => {
        if (!prev) return prev
        if (prev.resendCooldown <= 1) {
          stopCooldown()
          return { ...prev, resendCooldown: 0 }
        }
        return { ...prev, resendCooldown: prev.resendCooldown - 1 }
      })
    }, 1000)
  }, [stopCooldown])

  useEffect(() => {
    return () => {
      cleanupRecaptcha()
      stopCooldown()
    }
  }, [cleanupRecaptcha, stopCooldown])

  const resetMfa = useCallback(() => {
    resolverRef.current = null
    verificationIdRef.current = null
    cleanupRecaptcha()
    stopCooldown()
    setMfa(null)
  }, [cleanupRecaptcha, stopCooldown])

  const sendMfaCode = useCallback(async () => {
    const resolver = resolverRef.current
    if (!resolver) return

    const hint = resolver.hints.find(
      (h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID
    ) as PhoneMultiFactorInfo | undefined

    if (!hint) {
      resetMfa()
      setError('Sua conta exige um segundo fator não suportado por este app. Fale com um administrador.')
      return
    }

    setMfa((prev) => ({
      phoneHint: hint.phoneNumber ?? null,
      verifying: false,
      resendCooldown: prev?.resendCooldown ?? 0,
      sending: true,
    }))
    setError(null)

    try {
      cleanupRecaptcha()
      recaptchaRef.current = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
        size: 'invisible',
      })
      await recaptchaRef.current.render()

      const phoneAuthProvider = new PhoneAuthProvider(auth)
      verificationIdRef.current = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        recaptchaRef.current
      )

      setMfa({
        phoneHint: hint.phoneNumber ?? null,
        sending: false,
        verifying: false,
        resendCooldown: RESEND_COOLDOWN_SECONDS,
      })
      startCooldown()
    } catch (err: any) {
      console.error('[auth] Erro ao enviar SMS de verificação', err)
      setMfa((prev) => (prev ? { ...prev, sending: false } : prev))
      setError(describeAuthError(err?.code))
    }
  }, [cleanupRecaptcha, resetMfa, startCooldown])

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    try {
      const result = await signInWithPopup(auth, googleProvider)
      if (!result.user.email?.endsWith('@widde.io')) {
        await firebaseSignOut(auth)
        setUser(null)
        setError('Acesso restrito a contas @widde.io')
      }
    } catch (err: any) {
      if (err?.code === 'auth/multi-factor-auth-required') {
        resolverRef.current = getMultiFactorResolver(auth, err)
        await sendMfaCode()
        return
      }
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        return
      }
      console.error('[auth] Erro no login com Google', err)
      setError(describeAuthError(err?.code))
    }
  }, [sendMfaCode])

  const verifyMfaCode = useCallback(
    async (code: string) => {
      const resolver = resolverRef.current
      const verificationId = verificationIdRef.current
      if (!resolver || !verificationId) return

      setMfa((prev) => (prev ? { ...prev, verifying: true } : prev))
      setError(null)

      try {
        const credential = PhoneAuthProvider.credential(verificationId, code)
        const assertion = PhoneMultiFactorGenerator.assertion(credential)
        const result = await resolver.resolveSignIn(assertion)
        resetMfa()
        if (!result.user.email?.endsWith('@widde.io')) {
          await firebaseSignOut(auth)
          setUser(null)
          setError('Acesso restrito a contas @widde.io')
        }
      } catch (err: any) {
        console.error('[auth] Erro ao verificar código MFA', err)
        setMfa((prev) => (prev ? { ...prev, verifying: false } : prev))
        setError(describeAuthError(err?.code))
      }
    },
    [resetMfa]
  )

  const resendMfaCode = useCallback(async () => {
    await sendMfaCode()
  }, [sendMfaCode])

  const cancelMfa = useCallback(() => {
    resetMfa()
    setError(null)
  }, [resetMfa])

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth)
    setUser(null)
    setError(null)
  }, [])

  return {
    user,
    loading,
    error,
    mfa,
    signInWithGoogle,
    verifyMfaCode,
    resendMfaCode,
    cancelMfa,
    signOut,
  }
}
